import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/Auth';
import { Code } from '../../components/Code/Code';
import { API } from '../../API';
import { LoginPage2FASetup } from '../../components/LoginPage2FASetup/LoginPage2FASetup';
import { TWO_FA_MANDATORY_DATE } from '../../constants/twoFactorAuth';
import { ReactComponent as CloseIcon } from '../SearchPage/assets/close.svg';

export function LoginPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [otp2faEnabled, setOtp2faEnabled] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState(['']);
  const [code, setCode] = useState(['']);
  const [errors, setErrors] = useState({});
  const [needs2FASetup, setNeeds2FASetup] = useState(false);
  const [enrollmentData, setEnrollmentData] = useState(null);
  const auth = useAuth();

  // Where to land after a successful login. RequireAuth (and the OAuth consent
  // page) stash the location the user was trying to reach in state.from; that
  // location's *search* matters — the consent screen is identified solely by
  // its `request_id` query parameter, so dropping the query string would strand
  // the user on a broken authorize page.
  const redirectTarget = () => {
    const from = location.state?.from;
    if (!from?.pathname) {
      return '/';
    }
    return `${from.pathname}${from.search ?? ''}${from.hash ?? ''}`;
  };

  const completeLogin = () => {
    localStorage.removeItem('2fa_modal_dismissed');
    navigate(redirectTarget(), { replace: true });
  };

  // Login-driven enrollment: the workspace requires 2FA and the user is not
  // enrolled yet. The secret/QR came back from POST /API/v1/token; entering the
  // 6-digit code re-submits /token with the OTP to finish enrollment + sign in.
  const confirmEnrollment = async (codeStr) => {
    const tokenData = await auth.signIn({
      email,
      password,
      one_time_password: codeStr,
    });
    if (!tokenData?.access_token) {
      throw new Error('Enrollment failed');
    }
  };

  if (enrollmentData) {
    return (
      <LoginPage2FASetup
        initialOtpData={enrollmentData}
        onConfirmCode={confirmEnrollment}
        onComplete={completeLogin}
      />
    );
  }

  if (needs2FASetup) {
    return <LoginPage2FASetup />;
  }

  return (
    // translate="no": Chrome auto-translate wraps text nodes in <font> tags,
    // which crashes React's insertBefore when the form conditionally re-renders
    // (OSAVUL-FRONTEND-FK). The page is already localized via i18next.
    <div className="center" translate="no">
      <div className="login-form-wrapper">
        <h1>{t('Login')}</h1>

        <form
          method="post"
          onSubmit={async (event) => {
            event.preventDefault();

            let tokenData;
            try {
              tokenData = await auth.signIn({
                email,
                password,
                one_time_password: code.join(''),
              });
            } catch (error) {
              setErrors((errors) => ({ ...errors, general: error }));
              return;
            }

            if (tokenData?.detail === "One time password is required") {
              setOtp2faEnabled(true);
              return;
            } else if (tokenData?.detail) {
              setErrors((errors) => ({ ...errors, general: tokenData.detail }));
              return;
            }

            if (tokenData?.mfa_enrollment_required) {
              setEnrollmentData({
                otp_2fa_url: tokenData.otp_2fa_url,
                otp_2fa_secret: tokenData.otp_2fa_secret,
              });
              return;
            }

            if (
              tokenData?.access_token &&
              tokenData.workspace?.config?.two_factor_required &&
              new Date() >= TWO_FA_MANDATORY_DATE
            ) {
              try {
                const userData = await API.fetch('GET', '/API/v1/users/me');
                if (!userData?.otp_2fa_enabled) {
                  setNeeds2FASetup(true);
                  return;
                }
              } catch {
                // If user fetch fails, proceed normally
              }
            }

            localStorage.removeItem('2fa_modal_dismissed');
            navigate(redirectTarget(), { replace: true });
          }}
        >
          {otp2faEnabled ? (
            <div className="codeForm">
              <label>{t('One time password')}</label>
              <Code onChange={(c) => setCode(c)} />
            </div>
          ) : (
            <>
              <div className="txt_field">
                <input
                  className="input_filed"
                  name="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  autoComplete="off"
                  placeholder=" "
                  required
                />
                <span></span>
                <label>{t('Email')}</label>
              </div>
              <div className="txt_field">
                <input
                  className="input_filed"
                  name="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="off"
                  placeholder=" "
                  required
                />
                <span></span>
                <label>{t('Password')}</label>
              </div>
            </>
          )}
          <input type="submit" value={t('Login')} />

          {errors.general ? (
            <div className='errorMessage'>
              <span>{t(errors.general)}</span>
              <span onClick={() => setErrors({ ...errors, general: null })}>
                {' '}
                <CloseIcon />{' '}
              </span>
            </div>
          ) : (
            ''
          )}
        </form>
      </div>

    </div>
  );
}
